import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

// Animated electron transfer (ionic bond)
function AnimatedElectronTransfer({
  from = [-2, 0, 0],
  to = [2, 0, 0],
  start = false,
  duration = 2.5 // Longer for student clarity!
}) {
  const meshRef = useRef();
  useFrame(({ clock }) => {
    if (meshRef.current && start) {
      const t = Math.min(clock.getElapsedTime() / duration, 1);
      const x = from[0] + (to[0] - from[0]) * t;
      const y = from[1] + (to[1] - from[1]) * t + Math.sin(Math.PI * t) * 0.7;
      const z = from[2] + (to[2] - from[2]) * t;
      meshRef.current.position.set(x, y, z);
      meshRef.current.material.opacity = 1 - t * 0.2;
      if (t >= 1) meshRef.current.visible = false;
    }
  });
  return (
    <mesh ref={meshRef} position={from}>
      <sphereGeometry args={[0.21, 30, 30]} />
      <meshStandardMaterial color="#ffe066" emissive="#fffcc7" emissiveIntensity={0.65} opacity={0.99} transparent />
    </mesh>
  );
}

function MiniNucleus({ protons = 6, neutrons = 6 }) {
  const particles = [];
  const total = protons + neutrons;
  for (let i = 0; i < total; i++) {
    const angle = Math.random() * 2 * Math.PI;
    const z = (Math.random() - 0.5) * 0.35;
    const r = 0.20 + Math.random() * 0.13;
    const x = r * Math.cos(angle);
    const y = r * Math.sin(angle);
    const color = i < protons ? "#e74c3c" : "#90caf9";
    particles.push(
      <mesh key={i} position={[x, y, z]}>
        <sphereGeometry args={[0.20, 20, 20]} />
        <meshStandardMaterial
          color={color}
          metalness={0.16}
          roughness={0.27}
          emissive={i < protons ? "#c0392b" : "#bbdefb"}
          emissiveIntensity={i < protons ? 0.08 : 0.09}
        />
      </mesh>
    );
  }
  return <>{particles}</>;
}

function SimpleBohrModel({ element, shellConfig, position = [0,0,0], highlight = false }) {
  const baseRadius = 1.06;
  const shellGap = 0.36;
  const shells = shellConfig || element.shellConfig || [];
  const protons = element.protons;
  const neutrons = Array.isArray(element.neutrons)
    ? element.neutrons[0]
    : (element.neutrons || element.protons);

  return (
    <group position={position}>
      <MiniNucleus protons={protons} neutrons={neutrons} />
      {shells.map((n, idx) => (
        <group key={idx}>
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, idx * shellGap, 0]}>
            <torusGeometry args={[baseRadius + idx * 0.30, 0.009, 16, 60]} />
            <meshStandardMaterial color={highlight ? "#e6b333" : "#b6c7e6"} opacity={0.84} transparent />
          </mesh>
          {Array(n).fill(0).map((_, i) => {
            const angle = (2 * Math.PI * i) / n;
            return (
              <mesh
                key={i}
                position={[
                  (baseRadius + idx * 0.30) * Math.cos(angle),
                  idx * shellGap,
                  (baseRadius + idx * 0.30) * Math.sin(angle)
                ]}
              >
                <sphereGeometry args={[0.12, 20, 20]} />
                <meshStandardMaterial color="#3498db" emissive="#d6eaff" emissiveIntensity={0.08} />
              </mesh>
            );
          })}
        </group>
      ))}
    </group>
  );
}

export default function CompoundBohrVisualizer({
  donor,
  acceptor,
  showTransfer,
  donorLossShellConfig,
  acceptorGainShellConfig,
  animationDuration = 2.5 // Passable from parent, defaults to 2.5s
}) {
  if (
    !donor || !acceptor ||
    !donor.shellConfig || !acceptor.shellConfig
  ) {
    return <div style={{ color: "#b44", padding: "2em", textAlign: "center" }}>
      Loading atom data...
    </div>;
  }

  const donorPos = [-2, 0, 0];
  const acceptorPos = [2, 0, 0];
  const donorShellRadius = 1.06 + (donor.shellConfig.length - 1) * 0.30;
  const fromPos = [
    donorPos[0] + donorShellRadius,
    donorPos[1],
    donorPos[2]
  ];
  const acceptorShellRadius = 1.06 + (acceptor.shellConfig.length - 1) * 0.30;
  const toPos = [
    acceptorPos[0] + acceptorShellRadius * Math.cos(Math.PI / 6),
    acceptorPos[1] + 0.22,
    acceptorPos[2] + acceptorShellRadius * Math.sin(Math.PI / 6)
  ];

  return (
    <div style={{ width: 460, height: 350, margin: "0 auto" }}>
      <Canvas camera={{ position: [0, 0, 7.5] }}>
        <ambientLight intensity={0.8} />
        <pointLight position={[0, 6, 4]} intensity={0.8} />
        <SimpleBohrModel
          element={donor}
          shellConfig={showTransfer ? donorLossShellConfig : donor.shellConfig}
          position={donorPos}
          highlight={!showTransfer}
        />
        <SimpleBohrModel
          element={acceptor}
          shellConfig={showTransfer ? acceptorGainShellConfig : acceptor.shellConfig}
          position={acceptorPos}
          highlight={showTransfer}
        />
        {showTransfer && (
          <AnimatedElectronTransfer from={fromPos} to={toPos} start={true} duration={animationDuration} />
        )}
        <OrbitControls />
      </Canvas>
    </div>
  );
}
